import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { request } from '@/lib/api'
import { toast } from '@/components/ui/Toaster'
import type { AdminActionResult, AdminAreaSlug, AdminMe, ExportInput, ExportResult, Today } from './contract'
import type { FrenteKey } from '@/features/erp/contract'
import type { Related, RelatedRef, SearchAll } from './search-contract'
import { can, type Permission } from './permissions'

/* Spec COG-009 — Admin HTTP helpers and shared queries. Each area builds its own queries on top of these:
     const q = adminAreaQuery<FichasData>('fichas')            // GET /admin/fichas
     const act = useAdminAction<Body>('fichas', 'confirm')     // POST /admin/fichas/confirm, then refetches the area */

export const adminKeys = {
    me: ['panel', 'admin', 'me'] as const,
    today: ['panel', 'admin', 'hoje'] as const,
    area: (slug: AdminAreaSlug, ...rest: (string | number)[]) => ['panel', 'admin', slug, ...rest] as const,
}

export const adminGet = <T>(path: string) => request<T>('GET', `/admin${path}`)
export const adminPost = <T>(path: string, body?: unknown) => request<T>('POST', `/admin${path}`, body ?? {})

export const adminMeQuery = queryOptions({ queryKey: adminKeys.me, queryFn: () => adminGet<AdminMe>('/me'), staleTime: 60_000 })

/** GET /admin/<area>[?query] — refetched every 60 s like the other panel screens. */
export function adminAreaQuery<T>(slug: AdminAreaSlug, search = '') {
    return queryOptions({
        queryKey: adminKeys.area(slug, search),
        queryFn: () => adminGet<T>(`/${slug}${search ? `?${search}` : ''}`),
        refetchInterval: 60_000,
    })
}

/**
 * POST /admin/<area>/<action>; on success refetches the area (and Hoje) and shows the server's `message` as a toast.
 * Pass `undo` to offer "Desfazer" in that toast (it receives the same body and the result).
 * Pass `toast: false` when the screen shows the result itself.
 */
export function useAdminAction<TBody = Record<string, unknown>, TResult = AdminActionResult>(
    slug: AdminAreaSlug,
    action: string,
    options: { toast?: boolean; undo?: (body: TBody, result: TResult) => void } = {},
) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: TBody) => adminPost<TResult>(`/${slug}/${action}`, body),
        onSuccess: (result, body) => {
            void client.invalidateQueries({ queryKey: adminKeys.area(slug) })
            void client.invalidateQueries({ queryKey: adminKeys.today })
            const message = result && typeof result === 'object' && 'message' in result ? String((result as { message: unknown }).message) : null
            if (options.toast !== false && message) {
                toast({ message, action: options.undo ? { label: 'Desfazer', onClick: () => options.undo?.(body, result) } : undefined })
            }
        },
    })
}

export const adminTodayQuery = queryOptions({ queryKey: ['panel', 'admin', 'hoje'] as const, queryFn: () => adminGet<Today>('/hoje'), refetchInterval: 60_000 })

/** ⌘K: POST /admin/search { q, frente } — o texto vai no corpo, nunca na URL (search-contract.ts). */
export const adminSearchAllQuery = (q: string, frente: FrenteKey | null) =>
    queryOptions({
        queryKey: ['panel', 'admin', 'search', frente, q] as const,
        queryFn: () => adminPost<SearchAll>('/search', { q, ...(frente ? { frente } : {}) }),
        enabled: q.trim().length >= 2,
        staleTime: 30_000,
        retry: false,
    })

/** ⌘K: POST /admin/search/related { kind, id, frente } — o que está ligado ao resultado em destaque. */
export const adminRelatedQuery = (ref: RelatedRef | null, frente: FrenteKey | null) =>
    queryOptions({
        queryKey: ['panel', 'admin', 'search-related', frente, ref?.kind ?? '', ref?.id ?? ''] as const,
        queryFn: () => adminPost<Related>('/search/related', { ...ref!, ...(frente ? { frente } : {}) }),
        enabled: ref !== null,
        staleTime: 60_000,
        retry: false,
    })

/** POST /admin/<area>/export with the reason form. */
export function useAdminExport(slug: AdminAreaSlug) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: ExportInput) => adminPost<ExportResult>(`/${slug}/export`, body),
        onSuccess: () => client.invalidateQueries({ queryKey: adminKeys.area('equipe') }),
    })
}

export const meCan = (me: AdminMe | undefined, permission: Permission) => !!me && can(me.role, permission)
